import React from 'react';
import { Box, Card, DividerProps, Grid, Divider as MuiDivider, useMediaQuery } from '@material-ui/core';
import styled, { useTheme } from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useDateTimeService } from '../../../utils/hooks/dateTimeService';
import { useUser } from '../../../utils/hooks/auth';
import { Typography } from '../../../ui-kit';
import { VenueShift, closedShiftGuard } from './types';
import { CashFlowRightColumn } from './CashFlowRightColumn';
import CashDrawerOperations from './CashDrawerOperations';
import CashFlowLeftColumn from './CashFlowLeftColumn';
import ShiftInProgress from './ShiftInProgress';

const RelativeGrid = styled(Grid)`
    position: relative;
`;

const AbsoluteDivider = styled((props: Pick<DividerProps, 'orientation' | 'light'>) => (
    <MuiDivider {...props} />
))`
    &&{
        position: absolute;
        left: 50%;
        top: 15%;
        transform: translateX(-50%);
        height: 70%;
    }
`;

const Divider = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    if (isMobile) {
        return (
            <Grid item xs={12}>
                <MuiDivider light orientation='horizontal' variant='middle' />
            </Grid>
        );
    }

    return <AbsoluteDivider light orientation='vertical' />;
};


interface CashFlowListItemProps {
    venueShift: VenueShift;
}
const CashFlowListItem = ({ venueShift }: CashFlowListItemProps) => {
    const { id, startedBy, startedUtc, initialCashAmount, cashDrawerOperations } = venueShift;
    const { dateTimeService } = useDateTimeService();
    const { t } = useTranslation();
    const { user: { cultureCode } } = useUser();
    const startDate = dateTimeService.localizeDate(startedUtc, cultureCode);

    return (
        <Grid item xs={12} key={id}>
            <Card>
                <Box py={2}>
                    <Typography fontWeight='bold' align='center'>{t('Workday {{startDate}}', { startDate })}</Typography>
                </Box>
                <MuiDivider variant='middle' light />
                <RelativeGrid container spacing={3}>
                    <Grid item xs={12} sm={6}>
                        <CashFlowLeftColumn
                            startedBy={startedBy}
                            startedUtc={startedUtc}
                            initialCashAmount={initialCashAmount}
                        />
                    </Grid>
                    <Divider />
                    <Grid item xs={12} sm={6}>
                        {closedShiftGuard(venueShift)
                        ? (
                            <CashFlowRightColumn
                                cashIncome={venueShift.cashIncome}
                                finishedBy={venueShift.finishedBy}
                                finishedUtc={venueShift.finishedUtc}
                                expectedCashAmount={venueShift.expectedCashAmount}
                                finalCashAmount={venueShift.finalCashAmount}
                                expectedCreditCardAmount={venueShift.expectedCreditCardAmount}
                                finalCreditCardAmount={venueShift.finalCreditCardAmount}
                            />
                        ) : <ShiftInProgress />}
                    </Grid>
                </RelativeGrid>
                <MuiDivider variant='middle' light />
                <CashDrawerOperations cashDrawerOperations={cashDrawerOperations} />
            </Card>
        </Grid>
    );
};

export default CashFlowListItem;
